import { format, differenceInMonths, addMonths } from 'date-fns';

export interface InvoiceItem {
  description: string;
  amount: number;
}

export interface Invoice {
  id: string;
  studentId: string;
  studentName: string;
  invoiceNumber: string;
  items: InvoiceItem[];
  totalAmount: number;
  issueDate: string;
  dueDate: string;
  startDate: string;
  endDate: string;
  status: 'pending' | 'paid' | 'overdue';
  unitNo?: string;
}

export const generateInvoiceNumber = (): string => {
  return `INV-${format(new Date(), 'yyyyMM')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
};

export const calculateDueDate = (issueDate: Date): Date => {
  const dueDate = new Date(issueDate);
  dueDate.setDate(dueDate.getDate() + 14); // 14 days payment term
  return dueDate;
};

export const generateSingleInvoice = (
  student: {
    studentId: string;
    studentName: string;
    unitNo?: string;
  },
  startDate: Date,
  endDate: Date
): Invoice => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Calculate number of months between start and end dates
  const monthsDiff = differenceInMonths(end, start) + 1;
  const monthlyAmount = 5000.00;
  const totalAmount = monthlyAmount * monthsDiff;
  
  const periodDescription = format(start, 'dd MMM yyyy') === format(end, 'dd MMM yyyy')
    ? format(start, 'dd MMM yyyy')
    : `${format(start, 'dd MMM yyyy')} - ${format(end, 'dd MMM yyyy')}`;

  const invoiceItems: InvoiceItem[] = [
    {
      description: `Monthly Rent (All Services Included) for period: ${periodDescription} (${monthsDiff} month${monthsDiff > 1 ? 's' : ''})`,
      amount: totalAmount,
    }
  ];

  const issueDate = new Date();
  const dueDate = calculateDueDate(issueDate);

  return {
    id: crypto.randomUUID(),
    studentId: student.studentId,
    studentName: student.studentName,
    unitNo: student.unitNo,
    invoiceNumber: generateInvoiceNumber(),
    items: invoiceItems,
    totalAmount,
    issueDate: format(issueDate, 'yyyy-MM-dd'),
    dueDate: format(dueDate, 'yyyy-MM-dd'),
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(end, 'yyyy-MM-dd'),
    status: 'pending',
  };
};

export const generateRecurringInvoices = (
  studentData: {
    studentId: string;
    studentName: string;
    unitNo?: string;
  }[],
  startDate: Date,
  endDate: Date
): Invoice[] => {
  const invoices: Invoice[] = [];
  const currentDate = new Date(startDate);
  const monthlyAmount = 5000.00;

  while (currentDate <= endDate) {
    for (const student of studentData) {
      const monthEndDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const periodDescription = `${format(currentDate, 'dd MMM yyyy')} - ${format(monthEndDate, 'dd MMM yyyy')}`;
      
      const invoiceItems: InvoiceItem[] = [
        {
          description: `Monthly Rent (All Services Included) for period: ${periodDescription}`,
          amount: monthlyAmount,
        }
      ];

      const totalAmount = monthlyAmount; // One month's rent for recurring invoices
      const issueDate = new Date(currentDate);
      const dueDate = calculateDueDate(issueDate);

      invoices.push({
        id: crypto.randomUUID(),
        studentId: student.studentId,
        studentName: student.studentName,
        unitNo: student.unitNo,
        invoiceNumber: generateInvoiceNumber(),
        items: invoiceItems,
        totalAmount,
        issueDate: format(issueDate, 'yyyy-MM-dd'),
        dueDate: format(dueDate, 'yyyy-MM-dd'),
        startDate: format(currentDate, 'yyyy-MM-dd'),
        endDate: format(monthEndDate, 'yyyy-MM-dd'),
        status: 'pending',
      });
    }

    // Move to next month
    currentDate.setMonth(currentDate.getMonth() + 1);
    currentDate.setDate(1); // Reset to first day of month
  }

  return invoices;
};

export const exportInvoicesToCSV = (invoices: Invoice[]): string => {
  const headers = [
    'Invoice Number',
    'Student ID',
    'Student Name',
    'Unit No',
    'Issue Date',
    'Due Date',
    'Start Date',
    'End Date',
    'Total Amount',
    'Status',
  ];

  const rows = invoices.map((invoice) => [
    invoice.invoiceNumber,
    invoice.studentId,
    invoice.studentName,
    invoice.unitNo || 'N/A',
    invoice.issueDate,
    invoice.dueDate,
    invoice.startDate,
    invoice.endDate,
    invoice.totalAmount.toFixed(2),
    invoice.status,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(',')),
  ].join('\n');

  return csvContent;
};

export const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};